import DOMPurify from "dompurify";
import katex from "katex";
import "katex/dist/katex.min.css";

const renderLatex = (inputString) => {
    if (!inputString) return '';

    // Regex to find LaTeX patterns - prioritize block equations first
    const latexPattern = /(\$\$.*?\$\$|\\\[.*?\\\]|\$.*?\$|\\\(.*?\\\))/g;

    if (typeof inputString !== 'string') {
        return String(inputString || '');
    }

    let output = inputString.replace(latexPattern, (match) => {
        try {
            // Check if it's a block equation
            const isBlock = match.startsWith("$$") || match.startsWith("\\[");
            
            // Extract content by removing delimiters
            let content;
            if (match.startsWith("$$")) {
                content = match.slice(2, -2);
            } else if (match.startsWith("\\[")) {
                content = match.slice(2, -2);
            } else if (match.startsWith("$")) {
                content = match.slice(1, -1);
            } else if (match.startsWith("\\(")) {
                content = match.slice(2, -2);
            }

            // Skip if content is empty or just whitespace
            if (!content || !content.trim()) {
                return match;
            }

            // Render LaTeX using KaTeX with additional options for stability
            const rendered = katex.renderToString(content, {
                throwOnError: false,
                displayMode: isBlock,
                // Add options to prevent layout issues
                fleqn: false, // Don't flush equations left
                leqno: false, // Don't put equation numbers on left
                // Prevent overflow issues
                strict: false,
                // Trust certain commands to prevent errors
                trust: (context) => ['\\htmlId', '\\htmlClass'].includes(context.command),
                // Set max size to prevent huge equations from breaking layout
                // maxSize: 10,
                // Set max expand to prevent infinite expansion
                maxExpand: 100
            });

            // Wrap block equations in a container to prevent layout shifts
            if (isBlock) {
                return `<div class="katex-container" style="text-align: center; margin: 0.5em 0; overflow-x: auto; contain: layout;">${rendered}</div>`;
            }

            // For inline equations, wrap in span with proper styling
            return `<span class="katex-inline" style="display: inline-block; vertical-align: baseline;">${rendered}</span>`;

        } catch (e) {
            console.error("LaTeX rendering error:", e, "for content:", match);
            // Return the original match if rendering fails
            return match;
        }
    });

    // Sanitize the final output
    return DOMPurify.sanitize(output, {
        // Allow KaTeX-specific attributes and classes
        ADD_ATTR: ['aria-hidden', 'role', 'style', 'xmlns', 'viewBox', 'width', 'height', 'preserveAspectRatio', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'd', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'transform', 'data-mml-node', 'data-c'],
        ADD_TAGS: ['math', 'mi', 'mo', 'mn', 'ms', 'mtext', 'mrow', 'msup', 'msub', 'mfrac', 'msqrt', 'mroot', 'svg', 'line', 'path', 'rect', 'circle', 'g', 'use', 'defs', 'clipPath', 'image'],
        FORBID_TAGS: ['script', 'object', 'embed'],
        KEEP_CONTENT: true
    });
};

export default renderLatex;